import { Component, Input, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { PlannedShuttleBuss } from '../../../../../models/ShuttleBookingModel';
import { Order, OrderShuttleVM } from '../../../../../models/orderModel';
import { OrderShuttleService } from '../../../../../services/ordershuttle.service';
import { OrderShuttleBusService } from '../../../../../services/ordershuttlebus.service';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmDeleteComponent } from '../../../users/edit-user/confirm-delete/confirm-delete.component';
import { BackOffice24OrderShuttlebusEditComponent } from '../back-office24-ordershuttlebus-edit/back-office24-ordershuttlebus-edit.component';
import moment from 'moment';
import { EventQueueService, AppEventType, AppEvent } from '../../../../../services/eventqueue.service';
import { AuthService } from '../../../../../services/auth.service';
import { Router } from '@angular/router';
import { ShuttelbussService } from '../../../../../services/shuttlebuss.service';
import { MyConfigService } from '../../../../../services/my-config.service';

@Component({
  selector: 'app-back-office24-ordershuttlebus-details',
  templateUrl: './back-office24-ordershuttlebus-details.component.html',
  styleUrl: './back-office24-ordershuttlebus-details.component.css',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})

export class BackOffice24OrderShuttlebusDetailsComponent implements OnInit, OnDestroy {
  @Input() orderShuttleBus: PlannedShuttleBuss;
  @Input() organizationId: string;

  orderShuttlesBoarded: OrderShuttleVM[] = [];
  orderShuttlesBooked: OrderShuttleVM[] = [];
  orderShuttlesPickup: OrderShuttleVM[] = [];
  orderShuttlesOther: OrderShuttleVM[] = [];

  isBtnPlannedDisabled = true;
  isBtnActiveDisabled = true;
  isBtnArrivalDisabled = true;
  isBtnCancelledDisabled = true;
  isBtnQRDisabled = true;
  isBoardingDisabled = true;
  isLoading = false;

  showFlightDetailsBoarded = false;
  showFlightDetailsBooked = false;
  showFlightDetailsPickup = false;
  showFlightDetailsOther = false;

  showCustDetailsBoarded = false;
  showCustDetailsBooked = false;
  showCustDetailsPickup = false;
  showCustDetailsOther = false;

  intervalId: any;
  componentDestroyed$: Subject<boolean> = new Subject();
  order: Order;

  keyStatusDisplayMap: Record<string, string> = {
    'Error': '0',
    'FromCustomerManualToDo': '1',
    'FromCustomerManualDone': '2',
    'FromCustomerAutomatedNoKey': '3',
    'FromCustomerAutomatedKeyIn': '4',
    'FromCustomerAutomatedDone': '5',
    'ToCustomerManualToDo': '6',
    'ToCustomerManualDone': '7',
    'ToCustomerAutomatedNoKey': '8',
    'ToCustomerAutomatedKeyIn': '9',
    'ToCustomerAutomatedDone': '10',
    'FromCustomerManualKeyToSafe': '11'
  };

  constructor(
    public snackBar: MatSnackBar,
    public orderShuttleService: OrderShuttleService,
    public orderShuttleBusService: OrderShuttleBusService,
    public myConfigService: MyConfigService,
    public dialog: MatDialog,
    public eventQueue: EventQueueService,
    private authService: AuthService,
    public router: Router,
    public shuttelbussService: ShuttelbussService
  ) { }

  ngOnInit(): void {
    this.setOrderShuttleBus(this.orderShuttleBus);
    this.refreshOrderShuttles(this.orderShuttleBus.orderShuttleBusId);
    this.intervalId = setInterval(() => this.refreshOrderShuttles(this.orderShuttleBus.orderShuttleBusId), 60000);
  }

  isHotel(orderShuttle: OrderShuttleVM): boolean {
    if (!orderShuttle || !orderShuttle.organizationId) return false;

    const hotelOrgIds = [
      '417596f5-8fe5-4fa7-84dc-0af0ace72cf1', // Scandic
      '5bf79f25-27bf-4b5b-af83-b4a0233dab70', // ApartDirect
      '39218780-069a-4c6b-9925-22365a075c15', // Quality
      '942ce067-7848-4441-b746-8f6eed7395bb', // Quality XPO
      'd6b9d8c6-8fb5-41a5-ac2e-b3fc007a8251', // Aiden
      '55d7084d-3cf0-4ccc-9448-f924afcdf3bc'  // Aiden Short Term
    ];

    return hotelOrgIds.includes(orderShuttle.organizationId.toLowerCase());
  }

  isRedKeyStatus(status: string): boolean {
    return [
      'FromCustomerManualToDo',
      'FromCustomerAutomatedNoKey',
      'ToCustomerAutomatedNoKey'
    ].includes(status);
  }

  isGreenKeyStatus(status: string): boolean {
    return [
      'ToCustomerManualToDo',
      'FromCustomerManualDone',
      'FromCustomerAutomatedKeyIn',
    ].includes(status);
  }

  isBlueKeyStatus(status: string): boolean {
    return [
      'FromCustomerManualKeyToSafe'
    ].includes(status);
  }

  showButtonForKeyStatus(status: string): boolean {
    return status === 'FromCustomerManualToDo' || status === 'FromCustomerManualKeyToSafe' || status === 'ToCustomerManualToDo';
  }

  showTextForKeyStatus(status: string): boolean {
    return status === 'FromCustomerManualDone' || status === 'FromCustomerAutomatedNoKey' || status === 'FromCustomerAutomatedKeyIn' || status === 'ToCustomerManualDone' || status === 'ToCustomerAutomatedNoKey';
  }

  getOrderShuttlesBoarded(orderShuttleBusId: string) {
    //Needs to return boarded time
    this.orderShuttleService
      .getOrderShuttlesBoardedBus(orderShuttleBusId)
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe(orderShuttles => {

        orderShuttles.forEach(orderShuttle => {
          orderShuttle.shuttleDateNew = this.getNewTime(orderShuttle);
          orderShuttle.isReadyForPickup = this.getIsReadyForPickup(orderShuttle);
        });

        this.orderShuttlesBoarded = [];
        orderShuttles.sort(
          function (a, b) {
            return new Date(a.shuttleDateNew).valueOf() - new Date(b.shuttleDateNew).valueOf();
          }
        ).forEach(orderShuttle => {
          if (orderShuttle.shuttleDate == orderShuttle.shuttleDateNew) {
            orderShuttle.shuttleDateNew = null;
          }
          this.orderShuttlesBoarded.push(orderShuttle);
        });
      });
  }

  getOrderShuttlesPaidBus(orderShuttleBusId: string) {

    this.orderShuttleService
      .getOrderShuttlesPaidBus(orderShuttleBusId)
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe(orderShuttles => {
        orderShuttles.forEach(orderShuttle => {
          orderShuttle.shuttleDateNew = this.getNewTime(orderShuttle);
          orderShuttle.isReadyForPickup = this.getIsReadyForPickup(orderShuttle);
        });

        this.orderShuttlesBooked = [];

        orderShuttles.sort(
          function (a, b) {
            return new Date(a.shuttleDateNew).valueOf() - new Date(b.shuttleDateNew).valueOf();
          }
        ).forEach(orderShuttle => {
          if (orderShuttle.shuttleDate == orderShuttle.shuttleDateNew) {
            orderShuttle.shuttleDateNew = null;
          }
          this.orderShuttlesBooked.push(orderShuttle);
        });
      });
  }

  // Ready for Transfer
  getOrderShuttlesPickup() {

    this.orderShuttleService
      .getOrderShuttlesPickup()
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe(orderShuttles => {
        orderShuttles.forEach(orderShuttle => {
          orderShuttle.shuttleDateNew = this.getNewTime(orderShuttle);
          orderShuttle.isReadyForPickup = this.getIsReadyForPickup(orderShuttle);
        });

        this.orderShuttlesPickup = [];

        orderShuttles.sort(
          function (a, b) {
            return new Date(a.shuttleUpdated).valueOf() - new Date(b.shuttleUpdated).valueOf();
          }
        ).forEach(orderShuttle => {

          const orgDetails = this.myConfigService.getAllMyConfigOrganizations().find(org => org.organizationId == orderShuttle.organizationId)
          if (orgDetails.typeName === "ShuttleCustomer") {
            orderShuttle.passengerLandedOrBooked = orderShuttle?.shuttleDate;
          } else {
            orderShuttle.passengerLandedOrBooked = this.getLandedOrBooked(orderShuttle);
          }

          if (this.orderShuttleBus.routeType == 'DEPARTURE' && orderShuttle.productType == 'ShuttleDeparture') {
            if (moment(orderShuttle.shuttleDate) == moment(orderShuttle.shuttleDateNew)) {
              orderShuttle.shuttleDateNew = null;
            }
            this.orderShuttlesPickup.push(orderShuttle);
          }

          if (this.orderShuttleBus.routeType == 'ARRIVAL' && orderShuttle.productType == 'ShuttleArrival') {
            orderShuttle.shuttleDateNew = this.getNewTime(orderShuttle);
            orderShuttle.isReadyForPickup = this.getIsReadyForPickup(orderShuttle)
            this.orderShuttlesPickup.push(orderShuttle);
          }

        });

        this.orderShuttlesPickup.sort(
          function (a, b) {
            return new Date(a.shuttleUpdated).valueOf() - new Date(b.shuttleUpdated).valueOf();
          }
        );
      });
  }

  getLandedOrBooked(orderShuttle: OrderShuttleVM) {
    let addMinutes = 25;
    // let addMinutesBaggageArrived = 15;
    if (orderShuttle?.baggage > 0) {
      addMinutes = 45;
    }

    if (orderShuttle.productType == 'ShuttleArrival') {
      if (orderShuttle.arrivalVM != null) {
        if (orderShuttle.arrivalVM.estimatedArrival) {
          return moment(orderShuttle.arrivalVM.estimatedArrival).add(addMinutes, 'm').toDate();
        } else if (orderShuttle.arrivalVM.scheduledArrival) {
          return moment(orderShuttle.arrivalVM.scheduledArrival).add(addMinutes, 'm').toDate();
        }
      }

      return orderShuttle.shuttleDate;

    } else {
      return orderShuttle.shuttleDate;
    }
  }

  // Other Kund
  getOrderShuttlesOther() {

    let orderShuttlesOtherNew: OrderShuttleVM[] = [];

    const dateFrom = moment(moment().format('YYYY-MM-DD') + 'T00:00').add(-12, 'hours');
    const dateTo = moment(moment().format('YYYY-MM-DD') + 'T23:59').add(12, 'hours');

    this.orderShuttleService
      .getOrderShuttlesPaidOrganizationBetweenShuttleDate(this.organizationId, dateFrom.format('YYYY-MM-DDTHH:mm'), dateTo.format('YYYY-MM-DDTHH:mm'))
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe(orderShuttles => {

        orderShuttles.forEach(orderShuttle => {

          if (orderShuttle.status == 'Paid') {

            if (this.orderShuttleBus.routeType == 'DEPARTURE' && orderShuttle.productType == 'ShuttleDeparture') {
              orderShuttlesOtherNew.push(orderShuttle);
            }

            if (this.orderShuttleBus.routeType == 'ARRIVAL' && orderShuttle.productType == 'ShuttleArrival') {
              orderShuttle.shuttleDateNew = this.getNewTime(orderShuttle);
              orderShuttle.isReadyForPickup = this.getIsReadyForPickup(orderShuttle)
              orderShuttlesOtherNew.push(orderShuttle);
            }
          }
        });

        this.orderShuttlesOther = [];

        if (this.orderShuttleBus.routeType == 'DEPARTURE') {
          orderShuttlesOtherNew.sort(
            function (a, b) {
              return new Date(a.shuttleDate).valueOf() - new Date(b.shuttleDate).valueOf();
            }
          ).forEach(orderShuttle => {
            this.orderShuttlesOther.push(orderShuttle);
          });
        }

        if (this.orderShuttleBus.routeType == 'ARRIVAL') {
          orderShuttlesOtherNew.sort(
            function (a, b) {
              return new Date(a.shuttleDateNew).valueOf() - new Date(b.shuttleDateNew).valueOf();
            }
          ).forEach(orderShuttle => {
            if (moment(orderShuttle.shuttleDate) == moment(orderShuttle.shuttleDateNew)) {
              orderShuttle.shuttleDateNew = null;
            }
            this.orderShuttlesOther.push(orderShuttle);
          });
        }
      });
  }

  setOrderShuttleBoarded(orderShuttleId: string, orderShuttleBusId: string) {
    this.orderShuttleService
      .setOrderShuttleBoarded(orderShuttleId, orderShuttleBusId)
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe(orderShuttles => {
        this.refreshOrderShuttles(orderShuttleBusId);
      });
  }

  setOrderShuttleNoShow(orderShuttleId: string, orderShuttleBusId: string) {
    const dialogRef = this.dialog.open(ConfirmDeleteComponent, {
      data: {
        message: `Är du säker på att du vill ändra status till No Show?`,
        buttonText: {
          ok: 'Ja',
          cancel: 'Avbryt'
        }
      }
    });
    dialogRef.afterClosed().subscribe((confirmed: boolean) => {
      if (confirmed) {
        this.orderShuttleService
          .setOrderShuttleNoShow(orderShuttleId)
          .pipe(takeUntil(this.componentDestroyed$))
          .subscribe(orderShuttles => {
            this.refreshOrderShuttles(orderShuttleBusId);
          });
      }
    });
  }

  setOrderShuttleReadyForPickup(orderShuttleId: string, orderShuttleBusId: string) {
    const dialogRef = this.dialog.open(ConfirmDeleteComponent, {
      data: {
        message: `Är du säker på att du vill ångra incheckningen?`,
        buttonText: {
          ok: 'Ja',
          cancel: 'Avbryt'
        }
      }
    });
    dialogRef.afterClosed().subscribe((confirmed: boolean) => {
      if (confirmed) {
        this.orderShuttleService
          .setOrderShuttleReadyForPickup(orderShuttleId)
          .pipe(takeUntil(this.componentDestroyed$))
          .subscribe(orderShuttles => {
            this.refreshOrderShuttles(orderShuttleBusId);
          });
      }
    });
  }

  setOrderShuttlePaid(orderShuttleId: string, orderShuttleBusId: string) {
    const dialogRef = this.dialog.open(ConfirmDeleteComponent, {
      data: {
        message: `Är du säker på att du vill ångra incheckningen?`,
        buttonText: {
          ok: 'Ja',
          cancel: 'Avbryt'
        }
      }
    });
    dialogRef.afterClosed().subscribe((confirmed: boolean) => {
      if (confirmed) {
        this.orderShuttleService
          .setOrderShuttlePaid(orderShuttleId)
          .pipe(takeUntil(this.componentDestroyed$))
          .subscribe(orderShuttles => {
            this.refreshOrderShuttles(orderShuttleBusId);
          });
      }
    });
  }

  setBusPlanned(orderShuttleBusId: string) {
    const dialogRef = this.dialog.open(ConfirmDeleteComponent, {
      data: {
        message: `Är du säker på att du vill ändra status till Plannerad?`,
        buttonText: {
          ok: 'Ja',
          cancel: 'Avbryt'
        }
      }
    });
    dialogRef.afterClosed().subscribe((confirmed: boolean) => {
      if (confirmed) {
        this.orderShuttleBusService
          .setBusPlanned(orderShuttleBusId)
          .pipe(takeUntil(this.componentDestroyed$))
          .subscribe(orderShuttleBus => {
            this.setOrderShuttleBus(orderShuttleBus);
          });
      }
    });
  }

  setBusActive(orderShuttleBusId: string) {
    const dialogRef = this.dialog.open(ConfirmDeleteComponent, {
      data: {
        message: `Är du säker på att du vill ändra status till Aktiv?`,
        buttonText: {
          ok: 'Ja',
          cancel: 'Avbryt'
        }
      }
    });
    dialogRef.afterClosed().subscribe((confirmed: boolean) => {
      if (confirmed) {
        this.orderShuttleBusService
          .setBusActive(orderShuttleBusId)
          .pipe(takeUntil(this.componentDestroyed$))
          .subscribe(orderShuttleBus => {
            this.setOrderShuttleBus(orderShuttleBus);
          });
      }
    });
  }

  setBusDeparted(orderShuttleBusId: string) {
    const dialogRef = this.dialog.open(ConfirmDeleteComponent, {
      data: {
        message: `Är du säker på att du vill ändra status till Avgång?`,
        buttonText: {
          ok: 'Ja',
          cancel: 'Avbryt'
        }
      }
    });
    dialogRef.afterClosed().subscribe((confirmed: boolean) => {
      if (confirmed) {
        this.orderShuttleBusService
          .setBusDeparted(orderShuttleBusId)
          .pipe(takeUntil(this.componentDestroyed$))
          .subscribe(orderShuttleBus => {
            this.setOrderShuttleBus(orderShuttleBus);
          });
      }
    });
  }

  setBusArrived(orderShuttleBusId: string) {
    const dialogRef = this.dialog.open(ConfirmDeleteComponent, {
      data: {
        message: `Är du säker på att du vill ändra status till Ankomst?`,
        buttonText: {
          ok: 'Ja',
          cancel: 'Avbryt'
        }
      }
    });
    dialogRef.afterClosed().subscribe((confirmed: boolean) => {
      if (confirmed) {
        this.orderShuttleBusService
          .setBusArrived(orderShuttleBusId)
          .pipe(takeUntil(this.componentDestroyed$))
          .subscribe(orderShuttleBus => {
            this.setOrderShuttleBus(orderShuttleBus);
            this.eventQueue.dispatch(new AppEvent(AppEventType.BusCheckinClearTabs, true));
          });
      }
    });
  }

  setBusCancelled(orderShuttleBusId: string) {
    const dialogRef = this.dialog.open(ConfirmDeleteComponent, {
      data: {
        message: `Är du säker på att du vill ändra status till Cancelled?`,
        buttonText: {
          ok: 'Ja',
          cancel: 'Avbryt'
        }
      }
    });
    dialogRef.afterClosed().subscribe((confirmed: boolean) => {
      if (confirmed) {
        this.orderShuttleBusService
          .setBusCancelled(orderShuttleBusId)
          .pipe(takeUntil(this.componentDestroyed$))
          .subscribe(orderShuttleBus => {
            this.setOrderShuttleBus(orderShuttleBus);
            this.eventQueue.dispatch(new AppEvent(AppEventType.BusCheckinClearTabs, true));
          });
      }
    });
  }

  setOrderShuttleBus(orderShuttleBus: PlannedShuttleBuss) {
    this.orderShuttleBus = orderShuttleBus;

    this.isBoardingDisabled = true;
    this.isBtnPlannedDisabled = true;
    this.isBtnActiveDisabled = true;
    this.isBtnArrivalDisabled = true;
    this.isBtnCancelledDisabled = true;
    this.isBtnQRDisabled = true;

    if (this.orderShuttleBus.status == 'Planned') {
      this.isBtnActiveDisabled = false;
      this.isBtnCancelledDisabled = false;
    }

    if (this.orderShuttleBus.status == 'Active') {
      this.isBoardingDisabled = false;
      this.isBtnArrivalDisabled = false;
      this.isBtnCancelledDisabled = false;
      //this.isBtnQRDisabled = false;
    }

    if (this.orderShuttleBus.status == 'Departed') {
      this.isBoardingDisabled = false;
      this.isBtnActiveDisabled = false;
      this.isBtnCancelledDisabled = false;
      //this.isBtnQRDisabled = false;
    }

    if (this.orderShuttleBus.status == 'Arrived') {
    }

    if (this.orderShuttleBus.status == 'Cancelled') {
    }
  }

  changeShowCustDetailsBoarded($event) {
    this.showCustDetailsBoarded = $event.checked;
  }

  changeShowCustDetailsBooked($event) {
    this.showCustDetailsBooked = $event.checked;
  }

  changeShowCustDetailsPickup($event) {
    this.showCustDetailsPickup = $event.checked;
  }

  changeShowCustDetailsOther($event) {
    this.showCustDetailsOther = $event.checked;
  }

  changeShowFlightDetailsBoarded($event) {
    this.showFlightDetailsBoarded = $event.checked;
  }

  changeShowFlightDetailsBooked($event) {
    this.showFlightDetailsBooked = $event.checked;
  }

  changeShowFlightDetailsPickup($event) {
    this.showFlightDetailsPickup = $event.checked;
  }

  changeShowFlightDetailsOther($event) {
    this.showFlightDetailsOther = $event.checked;
  }

  getNewTime(orderShuttle: OrderShuttleVM): Date {

    // var compareDates: Date[] = [];
    var resultingDate: Date = null;
    if (orderShuttle.arrivalId) {

      let addMinutes = 25;
      let addMinutesBaggageArrived = 15;
      if (orderShuttle.baggage > 0) {
        addMinutes = 45;
      }

      if (orderShuttle.arrivalVM.status == 'New' || orderShuttle.arrivalVM.status == 'Scheduled' || orderShuttle.arrivalVM.status == 'Active' || orderShuttle.arrivalVM.status == 'Deleted' || orderShuttle.arrivalVM.status == 'Cancelled') {

        if (orderShuttle.arrivalVM.actualArrival) {
          resultingDate = moment(orderShuttle.arrivalVM.actualArrival).add(addMinutes, 'm').toDate();
        } else if (orderShuttle.arrivalVM.estimatedArrival) {
          resultingDate = moment(orderShuttle.arrivalVM.estimatedArrival).add(addMinutes, 'm').toDate();
        } else if (orderShuttle.arrivalVM.scheduledArrival) {
          resultingDate = moment(orderShuttle.arrivalVM.scheduledArrival).add(addMinutes, 'm').toDate();
        }
      }

      if (orderShuttle.arrivalVM.status == 'Landed' || orderShuttle.arrivalVM.status == 'FirstBaggage' || orderShuttle.arrivalVM.status == 'LastBaggage') {
        if (orderShuttle.arrivalVM.firstBag) {
          resultingDate = moment(orderShuttle.arrivalVM.firstBag).add(addMinutesBaggageArrived, 'm').toDate();
        } else if (orderShuttle.arrivalVM.firstBagEstimated) {
          resultingDate = moment(orderShuttle.arrivalVM.firstBagEstimated).add(addMinutesBaggageArrived, 'm').toDate();
        } else if (orderShuttle.arrivalVM.actualArrival) {
          resultingDate = moment(orderShuttle.arrivalVM.actualArrival).add(addMinutes, 'm').toDate();
        }
      }

      return resultingDate;
    }

    return new Date(orderShuttle.shuttleDate);
  }

  getIsReadyForPickup(orderShuttle: OrderShuttleVM): boolean {
    if (orderShuttle.arrivalVM) { //Flight exists
      if (orderShuttle.baggage > 0) { //Has Baggage
        if (orderShuttle.arrivalVM.firstBag != null) { //Firstbag set
          var firstBag = moment(orderShuttle.arrivalVM.firstBag);
          if (firstBag.isSameOrBefore(moment())) { //Firstbag is before now
            return true
          }
          else { //Firstbag is after now
            return false;
          }
        }
        else { //Firstbag not set yet
          return false;
        }
      }
      else { //No Baggage
        var pickupTime = moment(orderShuttle.shuttleDateNew).subtract(30, 'minutes');
        return pickupTime.isSameOrBefore(moment());
      }
    }
    else { //Flight does not exis
      var pickupTime = moment(orderShuttle.shuttleDateNew).subtract(30, 'minutes');
      return pickupTime.isSameOrBefore(moment());
    }
  }

  getNewTimeColor(orderShuttle: OrderShuttleVM): string {

    if (orderShuttle.shuttleDateNew) {
      if (moment(orderShuttle.shuttleDateNew) > moment(orderShuttle.shuttleDate)) {
        return 'color: red;';
      }
    }

    return '';
  }

  refreshOrderShuttles(orderShuttleBusId: string) {
    if (!this.authService.loggedIn()) {
      return;
    }
    this.getOrderShuttlesBoarded(orderShuttleBusId);
    this.getOrderShuttlesPaidBus(orderShuttleBusId)
    this.getOrderShuttlesPickup();
    this.getOrderShuttlesOther();
  }

  editOrderShuttleBus() {
    let dialogRef = this.dialog.open(BackOffice24OrderShuttlebusEditComponent, {
      height: '900px',
      width: '720px',
      hasBackdrop: true,
      data: this.orderShuttleBus
    });

    dialogRef.afterClosed().subscribe((orderShuttleBus: PlannedShuttleBuss) => {
      if (orderShuttleBus) {
        this.orderShuttleBus = orderShuttleBus;
      }
    });
  }

  removeLastPart(text) {
    const lastColonIndex = text.lastIndexOf(":");
    return lastColonIndex !== -1 ? text.slice(0, lastColonIndex) : text;
  }

  goToOrder(order: Order): void {
    this.order = order;
    const url = this.router.serializeUrl(this.router.createUrlTree([`/admin/back-office24-order/${order.orderId}`]));
    window.open(url, '_blank');
  }

  handleKeys(orderShuttle: any) {
    switch (orderShuttle.keyStatus) {
      case 'FromCustomerManualToDo':
        this.KeyIsTaken(orderShuttle);
        break;
      case 'FromCustomerManualKeyToSafe':
        this.keyTaskIsDone(orderShuttle);
        break;
      case 'ToCustomerManualToDo':
        this.keyTaskIsDone(orderShuttle);
        break;
    }
  }

  keyTaskIsDone(orderShuttleData: OrderShuttleVM) {
    this.shuttelbussService
      .keyTaskIsDone(this.organizationId, orderShuttleData?.orderLockerId)
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe(result => {
        if (result == true) {
          this.getOrderShuttlesBoarded(this.orderShuttleBus.orderShuttleBusId);
        }
      });
  }

  KeyIsTaken(orderShuttleData: OrderShuttleVM) {
    this.shuttelbussService
      .keyIsTaken(this.organizationId, orderShuttleData?.orderLockerId)
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe(result => {
        if (result == true) {
          this.getOrderShuttlesBoarded(this.orderShuttleBus.orderShuttleBusId);
        }
      });
  }

  displayRouteName(orderShuttle: OrderShuttleVM): string {
    const hotelMap: Record<string, string> = {
      '417596F5-8FE5-4FA7-84DC-0AF0ACE72CF1': 'Scandic',
      '5BF79F25-27BF-4B5B-AF83-B4A0233DAB70': 'ApartDirect',
      '39218780-069A-4C6B-9925-22365A075C15': 'Quality',
      'D6B9D8C6-8FB5-41A5-AC2E-B3FC007A8251': 'Aiden'
    };
    return hotelMap[orderShuttle.organizationId.toUpperCase()] || '';
  }

  displayHotelName(orgId: string): string {
    if (orgId == '942ce067-7848-4441-b746-8f6eed7395bb') {
      return 'Quality';
    }
    return '';
  }

  ngOnDestroy() {
    this.componentDestroyed$.next(true);
    this.componentDestroyed$.complete();
    clearInterval(this.intervalId);
  }
}
